import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Users, Plus, Search, Clock, Calendar, Shield, Stethoscope, Pencil, UserX, Timer, RefreshCw, AlertTriangle, Activity, Settings, FileDown, Award, X } from 'lucide-react';
import { CrewAccountControls, useCrewAccounts } from '../components/crew/CrewAccountControls';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const positions = [
    { value: 'captain', label: 'Capitan' },
    { value: 'first_officer', label: 'Primer Oficial' },
    { value: 'purser', label: 'Sobrecargo' },
    { value: 'flight_attendant', label: 'TCP' },
];

const periodLabels = { '24h': '24 Horas', '7d': '7 Días', '28d': '28 Días', '365d': 'Anual' };

function FDTLBar({ label, current, limit, pct }) {
    const color = pct >= 90 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : pct >= 70 ? 'bg-yellow-400' : 'bg-emerald-500';
    return (
        <div className="space-y-1">
            <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-mono font-semibold">{current}h / {limit}h</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <div className="text-right text-[10px] font-mono text-muted-foreground">{pct}%</div>
        </div>
    );
}

function StatusBadge({ status }) {
    const styles = {
        ok: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
        info: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
        warning: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
        critical: 'bg-red-500/10 text-red-600 border-red-500/30',
    };
    const labels = { ok: 'OK', info: 'INFO', warning: 'ALERTA', critical: 'CRÍTICO' };
    return <Badge className={`${styles[status] || styles.ok} text-[10px] font-bold`} data-testid={`fdtl-status-${status}`}>{labels[status] || status}</Badge>;
}

export default function CrewPage() {
    const { hasRole } = useAuth();
    const [crew, setCrew] = useState([]);
    const [users, setUsers] = useState([]);
    const [dutyRecords, setDutyRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [positionFilter, setPositionFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('crew');
    const [newCrew, setNewCrew] = useState({ user_id: '', position: '', base_airport: '', medical_expiry: '', license_expiry: '', type_ratings: [] });
    const [editCrew, setEditCrew] = useState(null);
    const [editCrewData, setEditCrewData] = useState({});
    // FDTL state
    const [fdtlData, setFdtlData] = useState(null);
    const [fdtlLoading, setFdtlLoading] = useState(false);
    const [fdtlConfig, setFdtlConfig] = useState(null);
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState('');
    const [accounts, reloadAccounts] = useCrewAccounts();

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [crewRes, usersRes, dutyRes] = await Promise.all([
                axios.get(`${API}/crew`),
                axios.get(`${API}/users`).catch(() => ({ data: [] })),
                axios.get(`${API}/duty-records`).catch(() => ({ data: [] }))
            ]);
            setCrew(crewRes.data);
            setUsers(usersRes.data);
            setDutyRecords(dutyRes.data);
        } catch { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    };

    const fetchFdtl = async () => {
        setFdtlLoading(true);
        try {
            const [checkRes, configRes] = await Promise.all([
                axios.get(`${API}/fdtl/check-all`).catch(() => ({ data: { crew: [], summary: {}, regulation: '' } })),
                axios.get(`${API}/fdtl/config`).catch(() => ({ data: {} }))
            ]);
            setFdtlData(checkRes.data);
            setFdtlConfig(configRes.data);
        } catch { toast.error('Error al cargar datos FDTL'); }
        finally { setFdtlLoading(false); }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'fdtl' && !fdtlData) fetchFdtl();
    };

    const handleCreateCrew = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...newCrew,
                medical_expiry: newCrew.medical_expiry ? new Date(newCrew.medical_expiry).toISOString() : null,
                license_expiry: newCrew.license_expiry ? new Date(newCrew.license_expiry).toISOString() : null,
                // Auditoria 6 - Item 9: type_ratings al crear (no solo en editar)
                type_ratings: Array.isArray(newCrew.type_ratings) ? newCrew.type_ratings : [],
            };
            await axios.post(`${API}/crew`, payload);
            toast.success('Tripulante agregado exitosamente');
            setDialogOpen(false);
            fetchData();
            setNewCrew({ user_id: '', position: '', base_airport: '', medical_expiry: '', license_expiry: '', type_ratings: [] });
            setNewCrewRatingType(''); setNewCrewRatingExp('');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al agregar tripulante'); }
    };

    // Auditoria 6 - Item 9: helpers para type_ratings en creación
    const [newCrewRatingType, setNewCrewRatingType] = useState('');
    const [newCrewRatingExp, setNewCrewRatingExp] = useState('');
    const addNewCrewRating = () => {
        const t = (newCrewRatingType || '').trim().toUpperCase();
        if (!t) { toast.error('Tipo de aeronave requerido'); return; }
        const entry = { aircraft_type: t };
        if (newCrewRatingExp) entry.expires_at = new Date(newCrewRatingExp).toISOString();
        const prev = newCrew.type_ratings || [];
        const filtered = prev.filter(r => (r.aircraft_type || '').toUpperCase() !== t);
        setNewCrew({ ...newCrew, type_ratings: [...filtered, entry] });
        setNewCrewRatingType(''); setNewCrewRatingExp('');
    };
    const removeNewCrewRating = (idx) => {
        const prev = newCrew.type_ratings || [];
        setNewCrew({ ...newCrew, type_ratings: prev.filter((_, i) => i !== idx) });
    };

    const openEditCrew = (c) => {
        setEditCrewData({
            position: c.position || '', base_airport: c.base_airport || '',
            license_number: c.license_number || '',
            medical_expiry: c.medical_expiry?.slice(0, 10) || '',
            license_expiry: c.license_expiry?.slice(0, 10) || '',
            // Auditoria 4 - Item 15: type ratings (entrada manual del admin)
            type_ratings: Array.isArray(c.type_ratings) ? c.type_ratings : [],
            name: getCrewName(c)
        });
        setEditCrew(c);
    };

    // Auditoria 4 - Item 15: agregar/quitar type ratings
    const [newRatingType, setNewRatingType] = useState('');
    const [newRatingExp, setNewRatingExp] = useState('');
    const addTypeRating = () => {
        const t = (newRatingType || '').trim().toUpperCase();
        if (!t) { toast.error('Tipo de aeronave requerido'); return; }
        const entry = { aircraft_type: t };
        if (newRatingExp) entry.expires_at = new Date(newRatingExp).toISOString();
        const prev = editCrewData.type_ratings || [];
        // evitar duplicados exactos por aircraft_type (reemplazar si ya existe)
        const filtered = prev.filter(r => (r.aircraft_type || '').toUpperCase() !== t);
        setEditCrewData({ ...editCrewData, type_ratings: [...filtered, entry] });
        setNewRatingType('');
        setNewRatingExp('');
    };
    const removeTypeRating = (idx) => {
        const prev = editCrewData.type_ratings || [];
        setEditCrewData({ ...editCrewData, type_ratings: prev.filter((_, i) => i !== idx) });
    };

    const saveEditCrew = async () => {
        if (!editCrew) return;
        try {
            await axios.put(`${API}/crew/${editCrew.id}`, {
                ...editCrewData,
                medical_expiry: editCrewData.medical_expiry ? new Date(editCrewData.medical_expiry).toISOString() : null,
                license_expiry: editCrewData.license_expiry ? new Date(editCrewData.license_expiry).toISOString() : null
            });
            toast.success('Tripulante actualizado');
            setEditCrew(null);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al actualizar'); }
    };

    const deactivateCrew = async (c) => {
        if (!window.confirm(`Dar de baja a ${getCrewName(c)}? (El historial se mantiene)`)) return;
        try {
            const res = await axios.delete(`${API}/crew/${c.id}`);
            toast.success(res.data.message);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const recalculateHours = async () => {
        try {
            await axios.post(`${API}/crew/recalculate-hours`);
            toast.success('Horas recalculadas exitosamente');
            fetchData();
            if (activeTab === 'fdtl') fetchFdtl();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al recalcular'); }
    };

    const applyPreset = async () => {
        if (!selectedPreset) return;
        try {
            await axios.put(`${API}/fdtl/config`, { preset: selectedPreset });
            toast.success('Regulacion FDTL actualizada');
            setConfigDialogOpen(false);
            fetchFdtl();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const exportFdtlPdf = async (crewId) => {
        try {
            const url = crewId ? `${API}/fdtl/export-pdf?crew_id=${crewId}` : `${API}/fdtl/export-pdf`;
            const res = await axios.get(url, { responseType: 'blob' });
            const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = `FDTL_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
            a.click();
            URL.revokeObjectURL(blobUrl);
            toast.success('PDF descargado');
        } catch { toast.error('Error al generar PDF'); }
    };

    const canManageCrew = hasRole('admin') || hasRole('crew_admin');
    const getPositionLabel = (position) => positions.find(p => p.value === position)?.label || position;
    const getExpiryStatus = (dateStr) => {
        if (!dateStr) return null;
        const daysUntil = Math.floor((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
        if (daysUntil < 0) return { label: 'Vencido', className: 'status-danger' };
        if (daysUntil < 30) return { label: `${daysUntil}d`, className: 'status-warning' };
        if (daysUntil < 90) return { label: `${daysUntil}d`, className: 'status-info' };
        return { label: `${daysUntil}d`, className: 'status-success' };
    };
    const getCrewName = (c) => {
        if (c.name) return c.name;
        if (c.first_name) return `${c.first_name} ${c.last_name || ''}`.trim();
        const user = users.find(u => u.id === c.user_id);
        return user ? `${user.first_name} ${user.last_name}` : 'Sin nombre';
    };
    const filteredCrew = crew.filter(c => {
        const name = getCrewName(c).toLowerCase();
        const matchesSearch = name.includes(searchTerm.toLowerCase()) ||
            (c.base_airport || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (c.license_number || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch && (positionFilter === 'all' || c.position === positionFilter);
    });

    if (loading) {
        return <div className="space-y-4 animate-fade-in">
            {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-lg skeleton-pulse" />)}
        </div>;
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="crew-page">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">Gestion de Tripulaciones</h1>
                    <p className="text-muted-foreground">{crew.length} tripulantes registrados</p>
                </div>
                {canManageCrew && (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={recalculateHours} data-testid="recalculate-hours-btn">
                            <RefreshCw className="w-4 h-4 mr-2" />Recalcular Horas
                        </Button>
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="btn-primary" data-testid="new-crew-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Tripulante</Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                                <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">Agregar Tripulante</DialogTitle></DialogHeader>
                                <form onSubmit={handleCreateCrew} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Usuario</Label>
                                        <Select value={newCrew.user_id} onValueChange={(v) => setNewCrew({ ...newCrew, user_id: v })}>
                                            <SelectTrigger data-testid="crew-user-select"><SelectValue placeholder="Seleccionar usuario" /></SelectTrigger>
                                            <SelectContent>
                                                {/* Auditoria 6 - Item 8: excluir usuarios ya-tripulantes */}
                                                {users.filter(u => ['pilot', 'crew_admin'].includes(u.role) && !crew.some(c => c.user_id === u.id)).map(u => (
                                                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</SelectItem>
                                                ))}
                                                {users.filter(u => ['pilot', 'crew_admin'].includes(u.role) && !crew.some(c => c.user_id === u.id)).length === 0 && (
                                                    <div className="px-2 py-1.5 text-xs text-muted-foreground">Todos los usuarios elegibles ya están dados de alta como tripulantes.</div>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Posicion</Label>
                                            <Select value={newCrew.position} onValueChange={(v) => setNewCrew({ ...newCrew, position: v })}>
                                                <SelectTrigger data-testid="crew-position-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                                <SelectContent>{positions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Base (ICAO)</Label>
                                            <Input placeholder="SKBO" value={newCrew.base_airport} onChange={(e) => setNewCrew({ ...newCrew, base_airport: e.target.value.toUpperCase() })} maxLength={4} required data-testid="crew-base-input" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2"><Label>Vencimiento Medico</Label><Input type="date" value={newCrew.medical_expiry} onChange={(e) => setNewCrew({ ...newCrew, medical_expiry: e.target.value })} data-testid="crew-medical-input" /></div>
                                        <div className="space-y-2"><Label>Vencimiento Licencia</Label><Input type="date" value={newCrew.license_expiry} onChange={(e) => setNewCrew({ ...newCrew, license_expiry: e.target.value })} data-testid="crew-license-input" /></div>
                                    </div>
                                    {/* Auditoria 6 - Item 9: type_ratings al crear tripulante */}
                                    <div className="border border-border/40 rounded-lg p-3 bg-muted/10 space-y-2">
                                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                            <Award className="w-3.5 h-3.5 text-primary" />
                                            Habilitaciones de Tipo (opcional)
                                        </div>
                                        <div className="space-y-1">
                                            {(newCrew.type_ratings || []).map((r, i) => (
                                                <div key={i} className="flex items-center justify-between gap-2 text-xs bg-background/60 border border-border/40 rounded px-2 py-1" data-testid={`new-crew-rating-${i}`}>
                                                    <div className="flex items-center gap-2">
                                                        <Badge className="bg-primary/15 text-primary text-[10px] uppercase">{r.aircraft_type}</Badge>
                                                        {r.expires_at && <span className="text-muted-foreground">Vence: {String(r.expires_at).slice(0, 10)}</span>}
                                                    </div>
                                                    <button type="button" onClick={() => removeNewCrewRating(i)} className="text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5" /></button>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                                            <div className="space-y-1">
                                                <Label className="text-[10px] uppercase text-muted-foreground">Tipo</Label>
                                                <Input value={newCrewRatingType} onChange={e => setNewCrewRatingType(e.target.value.toUpperCase())} placeholder="ATR-72" data-testid="new-crew-rating-type" />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-[10px] uppercase text-muted-foreground">Vence (opcional)</Label>
                                                <Input type="date" value={newCrewRatingExp} onChange={e => setNewCrewRatingExp(e.target.value)} data-testid="new-crew-rating-exp" />
                                            </div>
                                            <Button type="button" size="sm" onClick={addNewCrewRating} className="btn-primary" data-testid="new-crew-rating-add">
                                                <Plus className="w-3.5 h-3.5 mr-1" />Agregar
                                            </Button>
                                        </div>
                                    </div>
                                    <Button type="submit" className="w-full btn-primary" data-testid="create-crew-submit">Agregar Tripulante</Button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList>
                    <TabsTrigger value="crew" data-testid="crew-tab">Tripulantes</TabsTrigger>
                    <TabsTrigger value="fdtl" data-testid="fdtl-tab"><Activity className="w-3.5 h-3.5 mr-1" />FDTL</TabsTrigger>
                    <TabsTrigger value="duty" data-testid="duty-tab">Duty Records</TabsTrigger>
                </TabsList>

                {/* Crew List Tab */}
                <TabsContent value="crew" className="mt-6 space-y-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input placeholder="Buscar tripulante..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-crew" />
                        </div>
                        <Select value={positionFilter} onValueChange={setPositionFilter}>
                            <SelectTrigger className="w-full md:w-48" data-testid="position-filter"><SelectValue placeholder="Posicion" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas</SelectItem>
                                {positions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredCrew.length === 0 ? (
                            <Card className="card-aviation col-span-full"><CardContent className="py-12 text-center text-muted-foreground"><Users className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No se encontraron tripulantes</p></CardContent></Card>
                        ) : filteredCrew.map((c) => {
                            const medicalStatus = getExpiryStatus(c.medical_expiry);
                            const licenseStatus = getExpiryStatus(c.license_expiry);
                            const isExpired = (medicalStatus?.label === 'Vencido') || (licenseStatus?.label === 'Vencido');
                            return (
                                <Card key={c.id} className={`card-aviation relative ${isExpired ? 'border-red-500/60 bg-red-500/5' : ''}`} data-testid={`crew-card-${c.id}`}>
                                    {isExpired && (
                                        <div className="absolute top-0 left-0 right-0 h-1 bg-red-500 rounded-t-lg" />
                                    )}
                                    <CardContent className="p-4">
                                        {isExpired && (
                                            <div className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-red-500/10 border border-red-500/30 rounded-md" data-testid={`crew-expired-banner-${c.id}`}>
                                                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                                <span className="text-xs font-bold text-red-400 uppercase tracking-wider">No Utilizable — Documento Vencido</span>
                                            </div>
                                        )}
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><Users className="w-6 h-6 text-primary" /></div>
                                                <div>
                                                    <p className="font-semibold">{getCrewName(c)}</p>
                                                    <p className="text-sm text-muted-foreground">{getPositionLabel(c.position)}</p>
                                                </div>
                                            </div>
                                            <Badge variant="outline" className="font-mono">{c.base_airport}</Badge>
                                        </div>
                                        {canManageCrew && (
                                            <div className="flex items-center gap-1 mt-2 justify-end">
                                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openEditCrew(c)} data-testid={`edit-crew-${c.id}`}><Pencil className="w-3 h-3 mr-1" />Editar</Button>
                                                <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={() => deactivateCrew(c)} data-testid={`deactivate-crew-${c.id}`}><UserX className="w-3 h-3 mr-1" />Baja</Button>
                                            </div>
                                        )}
                                        <CrewAccountControls c={c} account={accounts[c.id]} onChanged={reloadAccounts} />
                                        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                                            <div className="flex items-center gap-2">
                                                <Clock className="w-4 h-4 text-muted-foreground" />
                                                <div>
                                                    <span className="font-mono font-semibold" data-testid={`crew-hours-${c.id}`}>{c.total_flight_hours?.toFixed(1) || '0.0'}</span>
                                                    <span className="text-[10px] text-muted-foreground ml-1">hrs vuelo</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Timer className="w-4 h-4 text-muted-foreground" />
                                                <div>
                                                    <span className="font-mono font-semibold" data-testid={`crew-duty-${c.id}`}>{c.total_duty_time?.toFixed(1) || '0.0'}</span>
                                                    <span className="text-[10px] text-muted-foreground ml-1">hrs duty</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                                <div>
                                                    <span className="font-mono font-semibold">{c.monthly_flight_hours?.toFixed(1) || '0.0'}</span>
                                                    <span className="text-[10px] text-muted-foreground ml-1">hrs/mes</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-border/50 flex gap-4 text-xs">
                                            <div className="flex items-center gap-2">
                                                <Stethoscope className="w-3 h-3" /><span>Medico:</span>
                                                {medicalStatus ? <Badge className={`${medicalStatus.className} text-[10px] px-1.5 py-0`}>{medicalStatus.label}</Badge> : <span className="text-muted-foreground">N/A</span>}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Shield className="w-3 h-3" /><span>Licencia:</span>
                                                {licenseStatus ? <Badge className={`${licenseStatus.className} text-[10px] px-1.5 py-0`}>{licenseStatus.label}</Badge> : <span className="text-muted-foreground">N/A</span>}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </TabsContent>

                {/* FDTL Tab */}
                <TabsContent value="fdtl" className="mt-6 space-y-4" data-testid="fdtl-tab-content">
                    <p className="text-sm text-muted-foreground max-w-3xl" data-testid="fdtl-help">Límites de tiempo de vuelo y servicio (FDTL): muestra cuántas horas lleva cada tripulante en las últimas 24 h, 7 y 28 días frente al límite de la regulación, para no asignar a nadie que lo exceda.</p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <Button variant="outline" onClick={fetchFdtl} data-testid="refresh-fdtl"><RefreshCw className="w-4 h-4 mr-2" />Actualizar</Button>
                        <Button variant="outline" onClick={() => exportFdtlPdf()} data-testid="export-fdtl-pdf"><FileDown className="w-4 h-4 mr-2" />Exportar PDF</Button>
                        {fdtlConfig && (
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="font-mono text-xs" data-testid="fdtl-regulation">{fdtlConfig.regulation || 'FAA Part 117'}</Badge>
                                {canManageCrew && (
                                    <Button variant="ghost" size="sm" onClick={() => setConfigDialogOpen(true)} data-testid="fdtl-config-btn"><Settings className="w-4 h-4" /></Button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Alert Summary */}
                    {fdtlData && (
                        <div className="grid grid-cols-4 gap-3">
                            <Card className="card-aviation"><CardContent className="p-3 text-center">
                                <p className="text-2xl font-bold font-mono" data-testid="fdtl-crew-count">{fdtlData.crew_count}</p>
                                <p className="text-[10px] text-muted-foreground uppercase">Tripulantes</p>
                            </CardContent></Card>
                            <Card className="card-aviation border-emerald-500/30"><CardContent className="p-3 text-center">
                                <p className="text-2xl font-bold font-mono text-emerald-500" data-testid="fdtl-ok-count">{fdtlData.crew_count - fdtlData.summary.info - fdtlData.summary.warning - fdtlData.summary.critical}</p>
                                <p className="text-[10px] text-muted-foreground uppercase">Normal</p>
                            </CardContent></Card>
                            <Card className="card-aviation border-amber-500/30"><CardContent className="p-3 text-center">
                                <p className="text-2xl font-bold font-mono text-amber-500" data-testid="fdtl-warning-count">{fdtlData.summary.warning + fdtlData.summary.info}</p>
                                <p className="text-[10px] text-muted-foreground uppercase">Alerta</p>
                            </CardContent></Card>
                            <Card className="card-aviation border-red-500/30"><CardContent className="p-3 text-center">
                                <p className="text-2xl font-bold font-mono text-red-500" data-testid="fdtl-critical-count">{fdtlData.summary.critical}</p>
                                <p className="text-[10px] text-muted-foreground uppercase">Critico</p>
                            </CardContent></Card>
                        </div>
                    )}

                    {fdtlLoading && <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-primary" /></div>}

                    {/* Crew FDTL Cards */}
                    {fdtlData && !fdtlLoading && (
                        <div className="space-y-4">
                            {fdtlData.crew.map((c) => (
                                <Card key={c.crew_id} className={`card-aviation ${c.status === 'critical' ? 'border-red-500/50 bg-red-500/5' : c.status === 'warning' ? 'border-amber-500/50 bg-amber-500/5' : ''}`} data-testid={`fdtl-card-${c.crew_id}`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${c.status === 'critical' ? 'bg-red-500/20' : c.status === 'warning' ? 'bg-amber-500/20' : 'bg-emerald-500/20'}`}>
                                                    {c.status === 'critical' ? <AlertTriangle className="w-5 h-5 text-red-500" /> :
                                                     c.status === 'warning' ? <AlertTriangle className="w-5 h-5 text-amber-500" /> :
                                                     <Activity className="w-5 h-5 text-emerald-500" />}
                                                </div>
                                                <div>
                                                    <p className="font-semibold">{c.crew_name}</p>
                                                    <p className="text-xs text-muted-foreground">{getPositionLabel(c.position)}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                            <StatusBadge status={c.status} />
                                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => exportFdtlPdf(c.crew_id)} data-testid={`export-fdtl-${c.crew_id}`}><FileDown className="w-3.5 h-3.5" /></Button>
                                        </div>
                                        </div>

                                        {/* Alert messages */}
                                        {c.alerts.length > 0 && (
                                            <div className="mb-4 space-y-1">
                                                {c.alerts.map((a, i) => (
                                                    <div key={i} className={`text-xs px-2 py-1 rounded ${a.level === 'critical' ? 'bg-red-500/10 text-red-600' : a.level === 'warning' ? 'bg-amber-500/10 text-amber-600' : 'bg-blue-500/10 text-blue-600'}`} data-testid={`fdtl-alert-${c.crew_id}-${i}`}>
                                                        {a.level === 'critical' ? 'CRÍTICO' : a.level === 'warning' ? 'ALERTA' : 'INFO'}: {a.message}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Period bars */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                            {Object.entries(c.periods).map(([period, data]) => (
                                                <div key={period} className="space-y-2">
                                                    <p className="text-xs font-bold uppercase text-muted-foreground">{periodLabels[period]}</p>
                                                    <FDTLBar label="Vuelo" current={data.flight_hours} limit={data.flight_limit} pct={data.flight_pct} />
                                                    <FDTLBar label="Duty" current={data.duty_hours} limit={data.duty_limit} pct={data.duty_pct} />
                                                    <p className="text-[10px] text-muted-foreground text-center">{data.flights} vuelo{data.flights !== 1 ? 's' : ''}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}

                    {!fdtlData && !fdtlLoading && (
                        <div className="text-center py-12 text-muted-foreground">
                            <Activity className="w-12 h-12 mx-auto mb-3 opacity-40" />
                            <p>Haz clic en &quot;Actualizar&quot; para ver el estado FDTL</p>
                        </div>
                    )}
                </TabsContent>

                {/* Duty Records Tab */}
                <TabsContent value="duty" className="mt-6 space-y-4">
                    <p className="text-sm text-muted-foreground max-w-3xl" data-testid="duty-help">Duty Records: registro diario del periodo de servicio de cada tripulante (presentación a liberación), con el tiempo de vuelo y los tramos realizados. Es la base para revisar descansos y cumplimiento.</p>
                    <Card className="card-aviation">
                        <CardHeader><CardTitle className="font-['Barlow_Condensed'] uppercase">Registros de Duty Time</CardTitle></CardHeader>
                        <CardContent>
                            {dutyRecords.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8" data-testid="duty-empty">No hay registros de duty time</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="table-aviation" data-testid="duty-table">
                                        <thead><tr><th>Tripulante</th><th>Fecha</th><th>Inicio</th><th>Fin</th><th>Tiempo Vuelo</th><th>Tramos</th><th>Tipo</th></tr></thead>
                                        <tbody>
                                            {dutyRecords.slice(0, 50).map((record) => {
                                                const crewMember = crew.find(c => c.id === record.crew_id);
                                                const u = crewMember ? users.find(x => x.id === crewMember.user_id) : null;
                                                const name = record.crew_name || crewMember?.name || (u ? `${u.first_name} ${u.last_name}` : 'N/A');
                                                return (
                                                    <tr key={record.id}>
                                                        <td>{name}</td>
                                                        <td className="font-mono">{record.date ? new Date(`${record.date.slice(0, 10)}T12:00:00`).toLocaleDateString('es') : '—'}</td>
                                                        <td className="font-mono">{new Date(record.duty_start).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</td>
                                                        <td className="font-mono">{new Date(record.duty_end).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</td>
                                                        <td className="font-mono">{Math.floor((record.flight_time_minutes || 0) / 60)}:{String((record.flight_time_minutes || 0) % 60).padStart(2, '0')}</td>
                                                        <td className="font-mono">{record.sectors ?? '—'}</td>
                                                        <td><Badge variant="outline">{record.duty_type === 'flight' ? 'Vuelo' : record.duty_type}</Badge></td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Edit Crew Dialog */}
            <Dialog open={!!editCrew} onOpenChange={() => setEditCrew(null)}>
                <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2"><Pencil className="w-5 h-5 text-primary" />Editar Tripulante</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5"><Label>Posicion</Label>
                            <Select value={editCrewData.position || ''} onValueChange={v => setEditCrewData({ ...editCrewData, position: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{positions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Base (ICAO)</Label><Input value={editCrewData.base_airport || ''} onChange={e => setEditCrewData({ ...editCrewData, base_airport: e.target.value.toUpperCase() })} maxLength={4} data-testid="edit-crew-base" /></div>
                            <div className="space-y-1.5"><Label>No. Licencia</Label><Input value={editCrewData.license_number || ''} onChange={e => setEditCrewData({ ...editCrewData, license_number: e.target.value })} data-testid="edit-crew-license" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Venc. Medico</Label><Input type="date" value={editCrewData.medical_expiry || ''} onChange={e => setEditCrewData({ ...editCrewData, medical_expiry: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label>Venc. Licencia</Label><Input type="date" value={editCrewData.license_expiry || ''} onChange={e => setEditCrewData({ ...editCrewData, license_expiry: e.target.value })} /></div>
                        </div>

                        {/* Auditoria 4 - Item 15: Type Ratings (entrada manual) */}
                        <div className="border border-border/40 rounded-lg p-3 bg-muted/10 space-y-2">
                            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                <Award className="w-3.5 h-3.5 text-primary" />
                                Habilitaciones de Tipo (Type Ratings)
                            </div>
                            {(editCrewData.type_ratings || []).length === 0 && (
                                <p className="text-xs text-muted-foreground">Sin habilitaciones registradas.</p>
                            )}
                            <div className="space-y-1">
                                {(editCrewData.type_ratings || []).map((r, i) => (
                                    <div key={i} className="flex items-center justify-between gap-2 text-xs bg-background/60 border border-border/40 rounded px-2 py-1" data-testid={`type-rating-row-${i}`}>
                                        <div className="flex items-center gap-2">
                                            <Badge className="bg-primary/15 text-primary text-[10px] uppercase">{r.aircraft_type}</Badge>
                                            {r.expires_at && (
                                                <span className="text-muted-foreground">Vence: {String(r.expires_at).slice(0, 10)}</span>
                                            )}
                                        </div>
                                        <button type="button" onClick={() => removeTypeRating(i)} className="text-red-400 hover:text-red-300" data-testid={`type-rating-remove-${i}`}>
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                                <div className="space-y-1">
                                    <Label className="text-[10px] uppercase text-muted-foreground">Tipo (ej. ATR-72)</Label>
                                    <Input value={newRatingType} onChange={e => setNewRatingType(e.target.value.toUpperCase())} placeholder="ATR-72" data-testid="type-rating-input-type" />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-[10px] uppercase text-muted-foreground">Vence (opcional)</Label>
                                    <Input type="date" value={newRatingExp} onChange={e => setNewRatingExp(e.target.value)} data-testid="type-rating-input-exp" />
                                </div>
                                <Button type="button" size="sm" onClick={addTypeRating} className="btn-primary" data-testid="type-rating-add">
                                    <Plus className="w-3.5 h-3.5 mr-1" />Agregar
                                </Button>
                            </div>
                            <p className="text-[10px] text-muted-foreground italic">
                                Ingresa manualmente las habilitaciones vigentes. Se validaran al asignar un tripulante como &quot;Operando&quot; segun el tipo de aeronave del vuelo.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditCrew(null)}>Cancelar</Button>
                        <Button onClick={saveEditCrew} className="btn-primary" data-testid="save-edit-crew">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* FDTL Config Dialog */}
            <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
                <DialogContent className="sm:max-w-[460px]">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2"><Settings className="w-5 h-5 text-primary" />Configuracion FDTL</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">Selecciona la regulacion base para los limites FDTL de tu aerolinea.</p>
                        <div className="space-y-2">
                            <Label>Regulacion</Label>
                            <Select value={selectedPreset} onValueChange={setSelectedPreset}>
                                <SelectTrigger data-testid="fdtl-preset-select"><SelectValue placeholder="Seleccionar regulacion" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="faa">FAA Part 117 (EE.UU.)</SelectItem>
                                    <SelectItem value="easa">EASA ORO.FTL (Union Europea)</SelectItem>
                                    <SelectItem value="dgac_gt">DGAC Guatemala (AIC A 49-17)</SelectItem>
                                    <SelectItem value="dgca_india">DGCA India (CAR Section 7)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {fdtlConfig && (
                            <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
                                <p className="font-bold text-sm">Limites actuales: {fdtlConfig.regulation}</p>
                                {Object.entries(fdtlConfig.limits || {}).map(([period, limits]) => (
                                    <div key={period} className="flex justify-between font-mono">
                                        <span className="text-muted-foreground">{periodLabels[period] || period}</span>
                                        <span>Vuelo: {limits.flight_hours}h | Duty: {limits.duty_hours}h</span>
                                    </div>
                                ))}
                                <div className="pt-1 border-t border-border/50 flex justify-between font-mono">
                                    <span className="text-muted-foreground">Descanso minimo</span>
                                    <span>{fdtlConfig.rest_minimum_hours}h</span>
                                </div>
                                <div className="flex justify-between font-mono">
                                    <span className="text-muted-foreground">WOCL</span>
                                    <span>{fdtlConfig.wocl_start} - {fdtlConfig.wocl_end}</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Cancelar</Button>
                        <Button onClick={applyPreset} className="btn-primary" disabled={!selectedPreset} data-testid="apply-fdtl-preset">Aplicar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
